/**
 * Campagne-attributie (utm_* parameters en Google Ads klik-ID's) — end-to-end
 * van boenkop.be naar de boeking in het portaal.
 *
 * De marketingsite vangt utm_* en de Google Ads klik-ID (gclid, of gbraid/wbraid
 * op iOS) op de landingspagina op, bewaart ze in sessionStorage en plakt ze aan
 * elke link naar het portaal. Hier doen we hetzelfde: bij het laden van de app
 * lezen we ze uit de URL, bewaren ze voor de duur van de browsersessie en
 * sturen ze mee bij het aanmaken van een reservering of eventinschrijving.
 *
 * Met de klik-ID kan een betaalde boeking achteraf als offline conversie in
 * Google Ads geïmporteerd worden (admin › export), zonder Google-tag op de site.
 *
 * Bewust sessionStorage en geen cookie: er is geen marketingcookie (en dus
 * geen extra toestemming) nodig, de data verlaat de browser pas op het moment
 * dat de klant zelf een boeking plaatst en verdwijnt zodra de tab sluit.
 */

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

/** Google Ads auto-tagging: gclid, en de privacyvriendelijke iOS-varianten. */
export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const

/**
 * Herkomst zonder campagneparameters: de externe referrer en de eerste pagina
 * van het bezoek. boenkop.be vangt ze first-touch op en geeft ze aan het
 * portaal door als `ref` en `lp`; zonder die parameters valt het portaal terug
 * op zijn eigen document.referrer en landingspagina.
 */
export const REFERRER_KEYS = ['referrer', 'landing_page'] as const

/**
 * De pagina op boenkop.be waar op de portaallink geklikt werd (last-click),
 * doorgegeven als `src`. Naast `lp` (first-touch): lp zegt waar het bezoek
 * begon, src vanaf welke pagina de klant naar het portaal ging.
 */
export const SOURCE_PAGE_KEYS = ['source_page'] as const

export const ATTRIBUTION_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS, ...REFERRER_KEYS, ...SOURCE_PAGE_KEYS] as const

export type UtmKey = (typeof UTM_KEYS)[number]
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number]
export type ReferrerKey = (typeof REFERRER_KEYS)[number]
export type SourcePageKey = (typeof SOURCE_PAGE_KEYS)[number]
export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]

/** URL-parameters waarmee boenkop.be referrer en landingspagina doorgeeft. */
const REFERRER_PARAMS: Record<ReferrerKey, string> = { referrer: 'ref', landing_page: 'lp' }

/** URL-parameter waarmee boenkop.be de klikpagina doorgeeft. */
const SOURCE_PAGE_PARAMS: Record<SourcePageKey, string> = { source_page: 'src' }

/** Alleen pagina's van de marketingsite zelf zijn een geldige klikpagina. */
const SITE_HOSTS = ['boenkop.be', 'www.boenkop.be']

export type Attribution = Partial<Record<AttributionKey, string>>

// Zelfde sleutel als op boenkop.be (aparte origin, dus aparte storage — maar
// zo blijft het herkenbaar bij debuggen).
const STORAGE_KEY = 'boenkop_utm'

// Matcht de backendvalidatie (App\Support\Attribution::MAX_LENGTH).
const MAX_LENGTH = 255

// Controletekens (incl. regeleinden) worden een spatie; de waarde moet één
// schone token zijn, ze belandt in adminschermen en CSV-exports.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]+/g

// Klik-ID's zijn opake base64url-achtige tokens; al de rest is rommel.
const CLICK_ID_PATTERN = /^[A-Za-z0-9_-]+$/

function sanitize(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const cleaned = value.replace(CONTROL_CHARS, ' ').trim()
  if (!cleaned) return null
  return cleaned.slice(0, MAX_LENGTH)
}

function sanitizeClickId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const cleaned = value.trim()
  if (!cleaned || cleaned.length > MAX_LENGTH || !CLICK_ID_PATTERN.test(cleaned)) return null
  return cleaned
}

function normalise(input: Record<string, unknown> | null | undefined): Attribution {
  const result: Attribution = {}
  if (!input) return result
  for (const key of UTM_KEYS) {
    const value = sanitize(input[key])
    if (value) result[key] = value
  }
  for (const key of CLICK_ID_KEYS) {
    const value = sanitizeClickId(input[key])
    if (value) result[key] = value
  }
  for (const key of REFERRER_KEYS) {
    const value = sanitizeUrl(input[key])
    if (value) result[key] = value
  }
  for (const key of SOURCE_PAGE_KEYS) {
    const value = sanitizeSitePage(input[key])
    if (value) result[key] = value
  }
  return result
}

/** Een URL op boenkop.be (zonder query/hash), anders null. */
function sanitizeSitePage(value: unknown): string | null {
  const url = sanitizeUrl(value)
  if (!url) return null
  try {
    return SITE_HOSTS.includes(new URL(url).hostname) ? url : null
  } catch {
    return null
  }
}

/** Alleen http(s)-URL's, zonder query/hash (daar kunnen tokens in zitten). */
function sanitizeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  try {
    const url = new URL(value.trim())
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    return (url.origin + url.pathname).slice(0, MAX_LENGTH)
  } catch {
    return null
  }
}

function currentHost(): string {
  return typeof window !== 'undefined' ? window.location.hostname : ''
}

/**
 * Referrer en landingspagina van dit bezoek als boenkop.be ze niet meegaf:
 * de eigen document.referrer (een andere host dan het portaal) en de huidige
 * pagina. Alleen ingevuld als er een referrer is; een directe portaalstart
 * zonder herkomst levert niets op.
 */
function ownReferrer(): Attribution {
  if (typeof document === 'undefined' || typeof window === 'undefined') return {}
  const referrer = sanitizeUrl(document.referrer)
  if (!referrer) return {}
  try {
    if (new URL(referrer).hostname === currentHost()) return {}
  } catch {
    return {}
  }
  const landing = sanitizeUrl(window.location.href)
  return landing ? { referrer, landing_page: landing } : { referrer }
}

function readStored(): Attribution {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return normalise(typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null)
  } catch {
    return {}
  }
}

function writeStored(attribution: Attribution): void {
  try {
    if (Object.keys(attribution).length === 0) {
      sessionStorage.removeItem(STORAGE_KEY)
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
    }
  } catch {
    // Storage kan uitstaan (privémodus, quota): attributie is dan best-effort.
  }
}

/**
 * Lees utm_* en klik-ID's uit de (landings)URL en bewaar ze voor deze browsersessie.
 *
 * Nieuwe parameters overschrijven eerder bewaarde waarden per veld
 * (last-touch binnen de sessie); zonder attributie in de URL blijft wat er was.
 * Geeft de resulterende attributie terug.
 */
export function captureAttribution(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): Attribution {
  const params = new URLSearchParams(search)
  const fromUrl: Record<string, unknown> = {}
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    if (params.has(key)) fromUrl[key] = params.get(key)
  }
  for (const key of REFERRER_KEYS) {
    const param = REFERRER_PARAMS[key]
    if (params.has(param)) fromUrl[key] = params.get(param)
  }
  for (const key of SOURCE_PAGE_KEYS) {
    const param = SOURCE_PAGE_PARAMS[key]
    if (params.has(param)) fromUrl[key] = params.get(param)
  }

  const captured = normalise(fromUrl)
  const stored = readStored()

  // Referrer en landingspagina zijn first-touch: wat er al is, blijft staan.
  // De klikpagina (src) is last-click: een nieuwe overschrijft de vorige.
  // Zonder doorgegeven herkomst nemen we die van dit bezoek zelf.
  if (!stored.referrer && !captured.referrer) {
    Object.assign(captured, ownReferrer())
  }
  if (stored.referrer) {
    delete captured.referrer
    delete captured.landing_page
  }

  if (Object.keys(captured).length === 0) return stored

  const merged: Attribution = { ...stored, ...captured }
  writeStored(merged)
  return merged
}

/** De attributie die voor deze browsersessie bekend is (leeg object als er geen is). */
export function getAttribution(): Attribution {
  return readStored()
}

export function clearAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // zie writeStored
  }
}

/**
 * Attributievelden om in een API-payload te spreiden, bv.
 * `api.post('/bookings', { ...bookingData, ...attributionPayload() })`.
 * Alleen aanwezige velden worden meegestuurd.
 */
export function attributionPayload(): Attribution {
  return getAttribution()
}

/**
 * Het pad van de klikpagina op boenkop.be (`/`, `/prijzen`, …) voor analytics,
 * of null als er geen is. Alleen het pad: de host is altijd boenkop.be en een
 * query/hash wordt al bij het opvangen weggelaten.
 */
export function sourcePagePath(): string | null {
  const page = getAttribution().source_page
  if (!page) return null
  try {
    return new URL(page).pathname
  } catch {
    return null
  }
}

/** Object met (optionele) attributievelden, zoals een Booking of EventRegistration uit de API. */
export type Attributed = Partial<Record<AttributionKey, string | null | undefined>>

const LABELS: Record<AttributionKey, string> = {
  utm_source: 'Bron',
  utm_medium: 'Medium',
  utm_campaign: 'Campagne',
  utm_term: 'Term',
  utm_content: 'Content',
  gclid: 'Google Ads klik-ID',
  gbraid: 'Google Ads klik-ID (iOS app)',
  wbraid: 'Google Ads klik-ID (iOS web)',
  referrer: 'Verwijzer',
  landing_page: 'Landingspagina',
  source_page: 'Klikpagina',
}

/** Host van de referrer ("www.google.com"), of een lege string. */
export function referrerHost(item: Attributed | null | undefined): string {
  if (!item?.referrer) return ''
  try {
    return new URL(item.referrer).hostname
  } catch {
    return ''
  }
}

export function hasAttribution(item: Attributed | null | undefined): boolean {
  return !!item && ATTRIBUTION_KEYS.some(key => !!item[key])
}

/** Of de klant via een Google Ads-advertentie binnenkwam (klik-ID aanwezig). */
export function hasGoogleAdsClick(item: Attributed | null | undefined): boolean {
  return !!item && CLICK_ID_KEYS.some(key => !!item[key])
}

/**
 * Korte weergave voor lijsten: "instagram / social · najaar-2026".
 * Een Google Ads-klik zonder utm-bron toont "Google Ads"; zonder campagne
 * maar met referrer de host ervan ("www.google.com").
 * Geeft een lege string terug als er geen attributie is.
 */
export function formatAttribution(item: Attributed | null | undefined): string {
  if (!item) return ''
  let channel = [item.utm_source, item.utm_medium].filter(Boolean).join(' / ')
  if (!channel && hasGoogleAdsClick(item)) channel = 'Google Ads'
  if (!channel) channel = referrerHost(item)
  return [channel, item.utm_campaign].filter(Boolean).join(' · ')
}

/** Volledige weergave (bv. voor een tooltip): één regel per aanwezig veld. */
export function attributionTitle(item: Attributed | null | undefined): string {
  if (!item) return ''
  return ATTRIBUTION_KEYS.filter(key => !!item[key])
    .map(key => `${LABELS[key]}: ${item[key]}`)
    .join('\n')
}

/** Label per attributieveld, voor detailweergaves. */
export function attributionEntries(item: Attributed | null | undefined): Array<{ key: AttributionKey; label: string; value: string }> {
  if (!item) return []
  return ATTRIBUTION_KEYS.filter(key => !!item[key]).map(key => ({ key, label: LABELS[key], value: item[key] as string }))
}
